import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { readStoredSession, resolveSession, wantsFreshLogin } from './session';
import { fetchManorState, startManor, submitManorStage, linkManorReport } from './api';
import { sendReport, extractReportIdFromResponse } from '../../functions/sendReport';

const errMsg = (e) => e?.response?.data?.message || e?.message || 'Something went wrong';

// Log the player in (otp / demo / saved session) — see session.js.
export const loginManor = createAsyncThunk('manor/login', async (_, { rejectWithValue }) => {
  try {
    return await resolveSession();
  } catch (e) {
    return rejectWithValue(errMsg(e));
  }
});

// Where is this player up to? (`started: false` until stage 1 has been entered.)
export const loadManorState = createAsyncThunk('manor/load', async (_, { getState, rejectWithValue }) => {
  try {
    const res = await fetchManorState(getState().manor.session);
    return res.state; // null when the player hasn't started
  } catch (e) {
    return rejectWithValue(errMsg(e));
  }
});

// Entering stage 1 creates the gameattach + stat3 rows (idempotent on the server).
export const startManorGame = createAsyncThunk('manor/start', async (_, { getState, rejectWithValue }) => {
  try {
    return (await startManor(getState().manor.session)).state;
  } catch (e) {
    return rejectWithValue(errMsg(e));
  }
});

// Push points/time to the ExtraMilePlay reports API from the browser (add on the first call, update after)
// and store the returned report id on the player's stat3 row — the same flow as Modak Marathon.
// Calls are queued so two of them can never both "add" a report for the same player.
let reportQueue = Promise.resolve();
export const syncReport = createAsyncThunk('manor/syncReport', (snapshot, { getState, dispatch }) => {
  const run = async () => {
    const { session, reportId } = getState().manor;
    if (!session || ['demobypass', 'DEMO'].includes(session.role)) return null;
    const response = await sendReport({
      sessionId: session.sessionId,
      organizationId: session.organizationId,
      userId: session.userId,
      role: session.role,
      token: session.token,
      gameId: session.gameId,
      name: session.name || '',
      points: snapshot.points,
      time: snapshot.time,
      reportId: reportId || undefined,
    });
    const id = extractReportIdFromResponse(response);
    if (id && id !== reportId) {
      dispatch(reportLinked(id));
      await linkManorReport(session, id);
    }
    return id || reportId;
  };
  const p = reportQueue.then(run, run);
  reportQueue = p.catch(() => {});
  return p;
});

// Moving on does not wait for the server: the reducer below advances the player the instant a stage is
// submitted (optimistic) and rolls back only if the save fails. Network hiccups are retried first.
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
export const submitStage = createAsyncThunk('manor/submit', async ({ round, points }, { getState, dispatch, rejectWithValue }) => {
  const session = getState().manor.session;
  let lastErr;
  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      const res = await submitManorStage(session, round, points);
      if (res.success) {
        dispatch(syncReport({ points: res.state.points, time: res.state.time }));
        return res;
      }
      if (res.returnData === 'ignored') {
        // Already recorded (e.g. an earlier attempt got through): just take the server's progress.
        const { state } = await fetchManorState(session);
        dispatch(syncReport({ points: state.points, time: state.time }));
        return { state };
      }
      return rejectWithValue(res.error || res.message || 'Stage not accepted');
    } catch (e) {
      // 4xx is a real answer from the server (not a dropped connection): don't retry it.
      if (e?.response && e.response.status < 500) return rejectWithValue(errMsg(e));
      lastErr = e;
      await sleep(400 * (attempt + 1));
    }
  }
  return rejectWithValue(errMsg(lastErr));
});

const hhmmss = (t) => [Math.floor(t / 3600), Math.floor((t % 3600) / 60), t % 60].map((n) => String(n).padStart(2, '0')).join(':');

/** What the server will say once it has recorded this round: next stage open, or game over after the last. */
function optimisticGame(game, round, points) {
  const stages = Array.from({ length: 5 }, (_, i) => game.stages?.[i] ?? null);
  stages[round - 1] = String(points);
  return {
    ...game,
    stages,
    points: (game.points || 0) + Number(points),
    time: hhmmss(game.elapsedSeconds || 0),
    nextStage: round >= 5 ? null : round + 1,
    gameover: round >= 5,
    optimistic: true,
  };
}

const slice = createSlice({
  name: 'manor',
  initialState: {
    session: wantsFreshLogin() ? null : readStoredSession(),
    authStatus: 'idle', // idle -> loading -> succeeded | failed
    authError: null,
    starting: false, // creating the player's rows
    startTried: false, // start is attempted once per login, never in a loop
    game: null,
    loaded: false,
    reportId: null, // id of this player's report on the ExtraMilePlay reports API
    reportTried: false, // the first report is attempted once per login from the shell; later stages retry
    previousGame: null, // progress before an in-flight submit, restored if it fails
    error: null,
  },
  reducers: {
    reportLinked: (s, a) => {
      s.reportId = a.payload;
    },
  },
  extraReducers: (b) => {
    b.addCase(loginManor.pending, (s) => {
      s.authStatus = 'loading';
    })
      .addCase(loginManor.fulfilled, (s, a) => {
        s.session = a.payload;
        s.authStatus = 'succeeded';
        s.authError = null;
        s.game = null; // progress belongs to whoever just logged in
        s.loaded = false;
        s.startTried = false;
        s.reportId = null;
        s.reportTried = false;
      })
      .addCase(loginManor.rejected, (s, a) => {
        s.authStatus = 'failed';
        s.authError = a.payload;
      })
      .addCase(loadManorState.fulfilled, (s, a) => {
      s.game = a.payload;
      if (a.payload?.reportId) s.reportId = a.payload.reportId;
      s.loaded = true;
      s.error = null;
    })
      .addCase(startManorGame.pending, (s) => {
        s.starting = true;
        s.startTried = true;
      })
      .addCase(startManorGame.fulfilled, (s, a) => {
        s.starting = false;
        s.game = a.payload;
        if (a.payload?.reportId) s.reportId = a.payload.reportId;
        s.loaded = true;
        s.error = null;
      })
      .addCase(submitStage.pending, (s, a) => {
        if (!s.game || s.game.nextStage !== a.meta.arg.round) return;
        s.previousGame = s.game;
        s.game = optimisticGame(s.game, a.meta.arg.round, a.meta.arg.points);
      })
      .addCase(submitStage.fulfilled, (s, a) => {
        s.game = a.payload.state;
        if (a.payload.state?.reportId) s.reportId = a.payload.state.reportId;
        s.previousGame = null;
      })
      .addCase(syncReport.pending, (s) => {
        s.reportTried = true;
      })
      .addCase(syncReport.rejected, (s, a) => {
        console.error('manor report failed:', a.error?.message);
      })
      .addCase(loadManorState.rejected, (s, a) => {
        s.loaded = true;
        s.error = a.payload;
      })
      .addCase(startManorGame.rejected, (s, a) => {
        s.starting = false;
        s.error = a.payload;
        console.error('manor start failed:', a.payload);
      })
      .addCase(submitStage.rejected, (s, a) => {
        if (s.previousGame) s.game = s.previousGame; // back to the stage they were on
        s.previousGame = null;
        s.error = a.payload;
      });
  },
});

export const { reportLinked } = slice.actions;
export default slice.reducer;
